// Sliding Window — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/twoPointers.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer wherever it clarifies the window's movement.

const slidingWindowProblems = [
  {
    id: 'maximum-average-subarray-i',
    title: 'Maximum Average Subarray I',
    difficulty: 'Easy',
    leetcode: { number: 643, url: 'https://leetcode.com/problems/maximum-average-subarray-i/' },
    statement:
      'Given an integer array nums and an integer k, find a contiguous subarray of length exactly k that has ' +
      'the maximum average value, and return that average.',
    example: {
      input: 'nums = [1, 12, -5, -6, 50, 3], k = 4',
      output: '12.75',
      note: 'The subarray [12, -5, -6, 50] sums to 51, and 51 / 4 = 12.75.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Sum Every Window From Scratch)',
        why:
          "The direct reading of the problem: for every window of size k, add up its elements and divide by k, " +
          "keeping the best average found. Correct, but every window re-adds k elements even though it overlaps " +
          "k − 1 of them with the previous window.",
        steps: [
          'For each start index from 0 to n − k, sum the k elements beginning there.',
          'Divide by k to get that window\'s average, and compare it to the best found so far.',
          'Every window costs O(k) to sum — O(n · k) total.',
        ],
        code:
`public double findMaxAverageBruteForce(int[] nums, int k) {
    double maxAvg = Double.NEGATIVE_INFINITY;
    for (int start = 0; start <= nums.length - k; start++) {
        int sum = 0;
        for (int i = start; i < start + k; i++) sum += nums[i];
        maxAvg = Math.max(maxAvg, (double) sum / k);
    }
    return maxAvg;
}`,
        complexity: { time: 'O(n · k)', space: 'O(1)' },
        whenToUse:
          "Only useful to confirm the average formula out loud — the moment you see \"fixed size k\" in the " +
          "problem statement, that's a direct signal to reach for the O(1)-per-slide fixed window instead.",
      },
      {
        name: 'Approach 2 — Optimized: Fixed-Size Sliding Window',
        why:
          "Build the first window's sum once, then for every slide, subtract the element leaving on the left " +
          "and add the element entering on the right — a single O(1) update instead of resumming k elements.",
        steps: [
          'Sum the first k elements to get the initial window sum.',
          'Track the maximum sum seen (comparing sums directly avoids repeated division — divide only once at the end).',
          'For every subsequent window, subtract the outgoing element and add the incoming one.',
          'Divide the best sum found by k for the final average.',
        ],
        visual: {
          arrays: [{ label: 'nums (k = 4)', values: [1, 12, -5, -6, 50, 3] }],
          varNames: ['sum', 'maxSum'],
          steps: [
            { highlights: { nums: { primary: [0, 1, 2, 3] } }, vars: { sum: 2, maxSum: 2 }, note: 'Initial window [0,3]: sum = 1+12-5-6 = 2.' },
            { highlights: { nums: { primary: [1, 2, 3, 4] } }, vars: { sum: 51, maxSum: 51 }, note: 'Slide: subtract nums[0]=1, add nums[4]=50. sum = 2-1+50 = 51. New max!' },
            { highlights: { nums: { primary: [2, 3, 4, 5] } }, vars: { sum: 42, maxSum: 51 }, note: 'Slide: subtract nums[1]=12, add nums[5]=3. sum = 51-12+3 = 42. Not better. Final max sum: 51 → average = 51/4 = 12.75.' },
          ],
        },
        code:
`public double findMaxAverage(int[] nums, int k) {
    int sum = 0;
    for (int i = 0; i < k; i++) sum += nums[i];
    int maxSum = sum;

    for (int right = k; right < nums.length; right++) {
        int left = right - k;
        sum = sum - nums[left] + nums[right];
        maxSum = Math.max(maxSum, sum);
    }
    return (double) maxSum / k;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        whenToUse:
          "This is the textbook fixed-size window — reach for it any time a problem names an exact window size " +
          "k and asks for a best sum, average, or count across every such window.",
      },
    ],
  },
  {
    id: 'longest-substring-at-most-k-distinct',
    title: 'Longest Substring with At Most K Distinct Characters',
    difficulty: 'Medium',
    leetcode: { number: 340, url: 'https://leetcode.com/problems/longest-substring-with-at-most-k-distinct-characters/' },
    statement:
      'Given a string s and an integer k, return the length of the longest substring that contains at most k ' +
      'distinct characters.',
    example: {
      input: 's = "eceba", k = 2',
      output: '3',
      note: '"ece" has exactly 2 distinct characters (e, c) and length 3 — the longest such substring.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Check Every Substring)',
        why:
          "For every substring, count its distinct characters directly and check whether it stays within the " +
          "limit k. Correct, but every substring gets its own count from scratch, ignoring how much overlap " +
          "exists between nearby substrings.",
        steps: [
          'For each start index, extend a running set of characters to every end index.',
          'The moment the set exceeds k distinct characters, stop extending from this start.',
          'Track the longest valid substring length found.',
          'Every substring\'s distinct count is built independently — O(n²) in the worst case.',
        ],
        code:
`public int longestSubstringBruteForce(String s, int k) {
    int maxLen = 0;
    for (int i = 0; i < s.length(); i++) {
        Set<Character> seen = new HashSet<>();
        for (int j = i; j < s.length(); j++) {
            seen.add(s.charAt(j));
            if (seen.size() > k) break;
            maxLen = Math.max(maxLen, j - i + 1);
        }
    }
    return maxLen;
}`,
        complexity: { time: 'O(n²)', space: 'O(k)' },
        whenToUse:
          "Only useful to confirm what \"at most k distinct\" means before optimizing — the repeated set-building " +
          "is exactly the wasted work a sliding window eliminates.",
      },
      {
        name: 'Approach 2 — Optimized: Variable Window With a Frequency Map',
        why:
          "Expand the window, tracking a live frequency map of characters inside it. The map's SIZE (not any " +
          "individual count) is what needs bounding — the moment there are more than k distinct keys, shrink " +
          "from the left until a key's count drops to zero and can be removed entirely, dropping the map back " +
          "to k distinct keys.",
        steps: [
          'Expand right, adding each character to a frequency map (or incrementing its count).',
          'While the map holds more than k distinct keys, shrink from the left — decrementing counts, and deleting a key once its count reaches zero.',
          'After any needed shrinking, the window is always valid — track its length as the max.',
          'Every character enters and leaves the window at most once — O(n).',
        ],
        visual: {
          arrays: [{ label: 's (k = 2)', values: ['e', 'c', 'e', 'b', 'a'] }],
          varNames: ['distinct', 'maxLen'],
          steps: [
            { highlights: { s: { primary: [0] } }, vars: { distinct: 1, maxLen: 1 }, note: "right=0 ('e'): freq={e:1}. 1 distinct, within k=2. Length 1." },
            { highlights: { s: { primary: [0, 1] } }, vars: { distinct: 2, maxLen: 2 }, note: "right=1 ('c'): freq={e:1,c:1}. 2 distinct, within k=2. Length 2." },
            { highlights: { s: { primary: [0, 1, 2] } }, vars: { distinct: 2, maxLen: 3 }, note: "right=2 ('e' again): freq={e:2,c:1}. Still 2 distinct. Length 3 — new max." },
            { highlights: { s: { primary: [2, 3] } }, vars: { distinct: 2, maxLen: 3 }, note: "right=3 ('b'): freq={e:2,c:1,b:1} — 3 distinct, over budget. Shrink: remove s[0]='e' (freq[e]→1, still present), left=1; still 3 distinct — remove s[1]='c' (freq[c]→0, deleted), left=2. Now freq={e:1,b:1}, 2 distinct. Window [2,3], length 2 — not better." },
            { highlights: { s: { primary: [4] } }, vars: { distinct: 2, maxLen: 3 }, note: "right=4 ('a'): freq={e:1,b:1,a:1} — 3 distinct again. Shrink: remove s[2]='e' (freq[e]→0, deleted), left=3. Now freq={b:1,a:1}, 2 distinct. Window [3,4], length 2 — not better. Final answer: 3." },
          ],
        },
        code:
`public int longestSubstring(String s, int k) {
    Map<Character, Integer> freq = new HashMap<>();
    int left = 0, maxLen = 0;

    for (int right = 0; right < s.length(); right++) {
        char c = s.charAt(right);
        freq.put(c, freq.getOrDefault(c, 0) + 1);

        while (freq.size() > k) {
            char lc = s.charAt(left);
            freq.put(lc, freq.get(lc) - 1);
            if (freq.get(lc) == 0) freq.remove(lc);
            left++;
        }
        maxLen = Math.max(maxLen, right - left + 1);
    }
    return maxLen;
}`,
        complexity: { time: 'O(n)', space: 'O(k)' },
        interviewNote: "Try the \"K Distinct Characters\" mode in this pattern's interactive playground to run this exact algorithm on your own string and k.",
        whenToUse:
          "This is the template for an entire family of problems — swap \"freq.size() > k\" for any other " +
          "window-validity condition (a zero-count budget, a sum threshold) and the rest of the skeleton stays identical.",
      },
    ],
  },
  {
    id: 'permutation-in-string',
    title: 'Permutation in String',
    difficulty: 'Medium',
    leetcode: { number: 567, url: 'https://leetcode.com/problems/permutation-in-string/' },
    statement:
      'Given two strings s1 and s2, return true if s2 contains a permutation of s1 as a contiguous substring — ' +
      'in other words, if one of s1\'s character rearrangements appears somewhere in s2.',
    example: {
      input: 's1 = "ab", s2 = "eidbaooo"',
      output: 'true',
      note: '"ba" (a rearrangement of "ab") appears in s2 starting at index 3.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Frequency-Check Every Window From Scratch)',
        why:
          "s1's length fixes the window size. For every window of that size in s2, build its character " +
          "frequency map from scratch and compare it to s1's frequency map — a match means a permutation was found.",
        steps: [
          'Build a frequency map for s1 once.',
          'For every window of s1\'s length in s2, build that window\'s frequency map by scanning it.',
          'Compare the two frequency maps — if identical, a permutation was found.',
          'Every window\'s frequency map is rebuilt from scratch — O(n · k).',
        ],
        code:
`public boolean checkInclusionBruteForce(String s1, String s2) {
    int k = s1.length();
    if (k > s2.length()) return false;
    Map<Character, Integer> need = buildFreq(s1);

    for (int start = 0; start <= s2.length() - k; start++) {
        Map<Character, Integer> window = buildFreq(s2.substring(start, start + k));
        if (window.equals(need)) return true;
    }
    return false;
}

private Map<Character, Integer> buildFreq(String s) {
    Map<Character, Integer> freq = new HashMap<>();
    for (char c : s.toCharArray()) freq.put(c, freq.getOrDefault(c, 0) + 1);
    return freq;
}`,
        complexity: { time: 'O(n · k)', space: 'O(k)' },
        whenToUse:
          "Only useful to confirm the frequency-matching idea out loud — rebuilding a whole map per window wastes " +
          "the overlap between adjacent windows, which is exactly what a fixed sliding window reuses.",
      },
      {
        name: 'Approach 2 — Optimized: Fixed-Size Window, Incremental Frequency Comparison',
        why:
          "The window size is fixed at s1.length(), which is exactly what a fixed-size sliding window is built " +
          "for. Slide the window across s2 one character at a time, updating its frequency map incrementally " +
          "(one character out, one character in) instead of rebuilding it, and compare against s1's frequency " +
          "map at every position.",
        steps: [
          'Build s1\'s frequency map once, and the first window\'s frequency map from s2.',
          'Compare the two maps — if equal, a permutation starts at index 0.',
          'For every slide, remove the outgoing character\'s count and add the incoming character\'s count in the window map.',
          'Compare again after each slide — the first match anywhere is the answer.',
        ],
        visual: {
          arrays: [{ label: 's2 (window size 2, target freq {a:1,b:1})', values: ['e', 'i', 'd', 'b', 'a', 'o', 'o', 'o'] }],
          varNames: [],
          steps: [
            { highlights: { s2: { primary: [0, 1] } }, note: 'Window [0,1] = "ei" → freq {e:1,i:1}. Not equal to target {a:1,b:1}.' },
            { highlights: { s2: { primary: [1, 2] } }, note: 'Slide: window [1,2] = "id" → freq {i:1,d:1}. Not equal.' },
            { highlights: { s2: { primary: [2, 3] } }, note: 'Slide: window [2,3] = "db" → freq {d:1,b:1}. Not equal.' },
            { highlights: { s2: { primary: [3, 4] } }, note: 'Slide: window [3,4] = "ba" → freq {b:1,a:1}. Equal to target! A permutation of "ab" found — return true.' },
          ],
        },
        code:
`public boolean checkInclusion(String s1, String s2) {
    int k = s1.length();
    if (k > s2.length()) return false;

    int[] need = new int[26];
    int[] window = new int[26];
    for (char c : s1.toCharArray()) need[c - 'a']++;
    for (int i = 0; i < k; i++) window[s2.charAt(i) - 'a']++;

    if (Arrays.equals(need, window)) return true;

    for (int right = k; right < s2.length(); right++) {
        window[s2.charAt(right) - 'a']++;
        window[s2.charAt(right - k) - 'a']--;
        if (Arrays.equals(need, window)) return true;
    }
    return false;
}`,
        complexity: { time: 'O(n · 26)', space: 'O(26) — fixed alphabet size' },
        interviewNote: "Comparing two 26-length arrays every slide is technically O(26), which is O(1) since the alphabet is fixed — mentioning that (rather than calling it \"O(n)\" without qualification) shows you understand why it's still linear overall. A further micro-optimization tracks a single \"matches\" counter instead of comparing full arrays, but it's rarely necessary to mention unless asked.",
        whenToUse:
          "Same fixed-window skeleton as Maximum Average Subarray, just with a frequency-array comparison instead " +
          "of a running sum — recognizing that s1.length() fixes the window size is the key first observation.",
      },
    ],
  },
  {
    id: 'minimum-window-substring',
    title: 'Minimum Window Substring',
    difficulty: 'Hard',
    leetcode: { number: 76, url: 'https://leetcode.com/problems/minimum-window-substring/' },
    statement:
      'Given two strings s and t, return the minimum-length substring of s that contains every character of t ' +
      '(including matching duplicate counts). If no such substring exists, return an empty string.',
    example: {
      input: 's = "ADOBECODEBANC", t = "ABC"',
      output: '"BANC"',
      note: '"BANC" is the shortest substring of s containing an A, a B, and a C.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Check Every Substring)',
        why:
          "For every possible substring, check whether it contains all of t's required characters (with enough " +
          "of each). Correct, but every substring is checked independently, with no memory of what the previous " +
          "substring already established.",
        steps: [
          'For each start index, grow a substring one character at a time.',
          'After each growth, check whether the substring now contains every character of t in sufficient quantity.',
          'The moment it does, that\'s the shortest valid substring starting there — record it and move to the next start.',
          'Every (start, end) pair does its own containment check — O(n²) substrings, each check adding more overhead.',
        ],
        code:
`public String minWindowBruteForce(String s, String t) {
    Map<Character, Integer> need = new HashMap<>();
    for (char c : t.toCharArray()) need.put(c, need.getOrDefault(c, 0) + 1);

    String best = "";
    for (int i = 0; i < s.length(); i++) {
        Map<Character, Integer> window = new HashMap<>();
        for (int j = i; j < s.length(); j++) {
            char c = s.charAt(j);
            window.put(c, window.getOrDefault(c, 0) + 1);
            if (containsAll(window, need)) {
                String candidate = s.substring(i, j + 1);
                if (best.isEmpty() || candidate.length() < best.length()) {
                    best = candidate;
                }
                break;
            }
        }
    }
    return best;
}

private boolean containsAll(Map<Character, Integer> window, Map<Character, Integer> need) {
    for (Map.Entry<Character, Integer> e : need.entrySet()) {
        if (window.getOrDefault(e.getKey(), 0) < e.getValue()) return false;
    }
    return true;
}`,
        complexity: { time: 'O(n² · |t|)', space: 'O(n)' },
        whenToUse:
          "Only useful to establish the containment check out loud — this is the hardest problem in the pattern " +
          "specifically because the naive version is easy to write but far too slow.",
      },
      {
        name: 'Approach 2 — Optimized: Variable Window With a "Formed" Counter',
        why:
          "Track two frequency maps — need (from t, built once) and window (the current window's live counts) " +
          "— plus a formed counter: how many of t's DISTINCT characters currently have enough copies in the " +
          "window. The window is valid exactly when formed equals the number of distinct characters t requires. " +
          "Expand right until valid, then greedily shrink left as far as possible while staying valid, recording " +
          "the shortest window seen at every valid moment.",
        steps: [
          'Build need from t once, and note required = the number of distinct characters t has.',
          'Expand right, updating window\'s counts. Whenever a character\'s count in window first reaches its required count in need, increment formed.',
          'While formed equals required, the window is valid: record it if it\'s the shortest seen, then shrink from the left — decrementing formed if removing a character drops it below its required count.',
          'Continue until right reaches the end of s; the shortest valid window recorded is the answer.',
        ],
        visual: {
          arrays: [{ label: 's (t = "ABC")', values: ['A', 'D', 'O', 'B', 'E', 'C', 'O', 'D', 'E', 'B', 'A', 'N', 'C'] }],
          varNames: ['formed', 'bestLen'],
          steps: [
            { highlights: { s: { primary: [0, 1, 2, 3, 4, 5] } }, vars: { formed: 3, bestLen: 6 }, note: 'right reaches index 5 (\'C\'): A, B, and C are all present — formed = 3 = required. First valid window [0,5] = "ADOBEC", length 6. Record as best.' },
            { highlights: { s: { primary: [1, 2, 3, 4, 5] } }, vars: { formed: 2, bestLen: 6 }, note: 'Shrink: remove s[0]=\'A\' — window no longer has enough A\'s, formed drops to 2. Stop shrinking (window invalid again). left=1.' },
            { highlights: { s: { primary: [6, 7, 8, 9, 10, 11, 12] } }, vars: { formed: 3, bestLen: 6 }, note: 'Expanding continues through indices 6–11 without regaining all 3 characters (A was removed from the window). At right=12 (\'C\' again), A, B, and C are all present once more — formed = 3. Window [6,12] = "ODEBANC", length 7 — not better than 6.' },
            { highlights: { s: { primary: [7, 8, 9, 10, 11, 12] } }, vars: { formed: 3, bestLen: 6 }, note: 'Shrink: remove s[6]=\'O\' (not required, formed unaffected). Window [7,12] = "DEBANC", length 6 — a tie, not strictly better.' },
            { highlights: { s: { primary: [8, 9, 10, 11, 12] } }, vars: { formed: 3, bestLen: 5 }, note: 'Shrink: remove s[7]=\'D\' (not required). Window [8,12] = "EBANC", length 5 — shorter! New best.' },
            { highlights: { s: { primary: [9, 10, 11, 12] } }, vars: { formed: 3, bestLen: 4 }, note: 'Shrink: remove s[8]=\'E\' (not required). Window [9,12] = "BANC", length 4 — shorter again! New best.' },
            { highlights: { s: { primary: [10, 11, 12] } }, vars: { formed: 2, bestLen: 4 }, note: 'Shrink: remove s[9]=\'B\' — window no longer has enough B\'s, formed drops to 2. Stop shrinking. right has reached the end of s. Final answer: "BANC", length 4.' },
          ],
        },
        code:
`public String minWindow(String s, String t) {
    if (s.isEmpty() || t.isEmpty()) return "";

    Map<Character, Integer> need = new HashMap<>();
    for (char c : t.toCharArray()) need.put(c, need.getOrDefault(c, 0) + 1);
    int required = need.size();

    Map<Character, Integer> window = new HashMap<>();
    int formed = 0, left = 0, bestLen = Integer.MAX_VALUE, bestStart = 0;

    for (int right = 0; right < s.length(); right++) {
        char c = s.charAt(right);
        window.put(c, window.getOrDefault(c, 0) + 1);
        if (need.containsKey(c) && window.get(c).intValue() == need.get(c).intValue()) {
            formed++;
        }

        while (formed == required) {
            if (right - left + 1 < bestLen) {
                bestLen = right - left + 1;
                bestStart = left;
            }
            char lc = s.charAt(left);
            window.put(lc, window.get(lc) - 1);
            if (need.containsKey(lc) && window.get(lc) < need.get(lc)) {
                formed--;
            }
            left++;
        }
    }
    return bestLen == Integer.MAX_VALUE ? "" : s.substring(bestStart, bestStart + bestLen);
}`,
        complexity: { time: 'O(n)', space: 'O(|s1| + |s2|) — bounded by the alphabet in practice' },
        interviewNote: "The formed counter is the trick worth explaining clearly: it tracks distinct SATISFIED characters, not total character count, which is what lets the while loop's condition (formed == required) be checked in O(1) instead of re-scanning the whole need map on every step.",
        whenToUse:
          "This is the flagship variable-window problem — whenever a problem asks for the shortest window " +
          "satisfying a multi-character containment condition, this need/window/formed skeleton is the answer.",
      },
    ],
  },
];

export default slidingWindowProblems;
